// Zustand store for AvRi state management

import { create } from 'zustand';
import { api } from '../lib/api.js';

export const useAuthStore = create((set, get) => ({
  user: null,
  isLoading: false,
  isAuthenticated: false,

  // Initialize auth state from stored token
  init: async () => {
    set({ isLoading: true });
    const token = localStorage.getItem('avri_auth_token');

    if (token) {
      api.setToken(token);
      try {
        const { user } = await api.getCurrentUser();
        set({ user, isAuthenticated: true, isLoading: false });
        return;
      } catch (error) {
        console.error('[auth:init] failed to fetch user', error);
        // Token is invalid, clear it
        api.setToken(null);
      }
    }

    set({ user: null, isAuthenticated: false, isLoading: false });
  },

  // Send magic link
  requestMagicLink: async (email, name) => {
    set({ isLoading: true });
    try {
      const result = await api.requestMagicLink(email, name);
      set({ isLoading: false });
      return result;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  // Verify magic link and log in
  verifyMagicLink: async (token) => {
    set({ isLoading: true });
    try {
      const { user } = await api.verifyMagicLink(token);
      set({ user, isAuthenticated: true, isLoading: false });
      return user;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  // Log out
  logout: () => {
    api.logout();
    set({ user: null, isAuthenticated: false });
  },
}));

export const useHouseholdStore = create((set, get) => ({
  household: null,
  isLoading: false,

  // Load household profile
  loadHousehold: async () => {
    set({ isLoading: true });
    try {
      const { household } = await api.getHouseholdProfile();
      set({ household, isLoading: false });
      return household;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  // Update household profile
  updateProfile: async (profileData) => {
    set({ isLoading: true });
    try {
      const { household } = await api.updateHouseholdProfile(profileData);
      set({ household, isLoading: false });
      return household;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  // Update savings amount
  updateSavings: async (currentSavings) => {
    try {
      const { household } = await api.updateSavings(currentSavings);
      set({ household });
      return household;
    } catch (error) {
      throw error;
    }
  },

  // Get savings progress
  getSavingsProgress: () => {
    const { household } = get();
    if (!household) return null;
    
    const current = household.current_savings || 0;
    const target = household.target_down_payment || 0;
    
    if (target === 0) return null;
    
    const percentage = Math.min((current / target) * 100, 100);
    const remaining = Math.max(target - current, 0);
    
    return {
      current,
      target,
      percentage,
      remaining
    };
  },
}));

export const useHousesStore = create((set, get) => ({
  houses: [],
  isLoading: false,

  // Load saved houses
  loadHouses: async () => {
    set({ isLoading: true });
    try {
      const { houses } = await api.getHouses();
      set({ houses, isLoading: false });
      return houses;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  // Load a single house (used for detail view refresh)
  loadHouseById: async (houseId) => {
    try {
      const { house } = await api.getHouse(houseId);
      const { houses } = get();

      const existingIndex = houses.findIndex((item) => item.id === houseId);
      if (existingIndex >= 0) {
        const copy = [...houses];
        copy[existingIndex] = house;
        set({ houses: copy });
      } else {
        set({ houses: [house, ...houses] });
      }

      return house;
    } catch (error) {
      throw error;
    }
  },

  // Save a new house
  saveHouse: async (houseData) => {
    set({ isLoading: true });
    try {
      const { house } = await api.saveHouse(houseData);
      const { houses } = get();
      set({ houses: [house, ...houses], isLoading: false });
      return house;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  // Update existing house
  updateHouse: async (houseId, houseData) => {
    try {
      const { house } = await api.updateHouse(houseId, houseData);
      const { houses } = get();
      const updatedHouses = houses.map(h => h.id === houseId ? house : h);
      set({ houses: updatedHouses });
      return house;
    } catch (error) {
      throw error;
    }
  },

  // Delete house
  deleteHouse: async (houseId) => {
    try {
      await api.deleteHouse(houseId);
      const { houses } = get();
      const filteredHouses = houses.filter(h => h.id !== houseId);
      set({ houses: filteredHouses });
    } catch (error) {
      throw error;
    }
  },

  // Add reaction to house
  addReaction: async (houseId, reaction) => {
    try {
      await api.addReaction(houseId, reaction);
      await get().loadHouseById(houseId);
    } catch (error) {
      throw error;
    }
  },

  // Create share link
  createShareLink: async (houseId) => {
    try {
      const { shareLink } = await api.createShareLink(houseId);
      return shareLink;
    } catch (error) {
      throw error;
    }
  },
}));
